<template>
    <AppDialog v-model="isOpen" size="lg" @close="resetAll">
        <template #header>
            <h2 class="text-lg font-semibold text-theme-base">
                {{ modalTitle }}
            </h2>
            <p v-if="step === 'list'" class="mt-1 text-sm text-theme-muted">
                Elige una plantilla para armar tu catálogo más rápido.
            </p>
            <p
                v-if="step === 'catalog-mode'"
                class="mt-1 text-sm text-theme-muted"
            >
                Ya tienes bloques en el editor. Elige cómo aplicar esta
                plantilla.
            </p>
        </template>

        <div v-if="step === 'list'">
            <h3 class="text-sm font-semibold text-theme-base mb-2">
                Catálogo completo
            </h3>
            <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <button
                    v-for="template in catalogTemplates"
                    :key="template.id"
                    type="button"
                    class="flex flex-col overflow-hidden rounded-xl border border-theme-border text-left transition hover:border-theme-primary hover:shadow-sm"
                    @click="chooseCatalogTemplate(template)"
                >
                    <span
                        :title="template.description"
                        class="border-b border-theme-border px-3 py-2 text-sm font-semibold text-theme-base"
                    >
                        {{ template.name }}
                    </span>
                    <div
                        class="pointer-events-none max-h-64 space-y-2 overflow-y-auto"
                    >
                        <segment-preview
                            v-for="(segment, index) in template.segments"
                            :key="index"
                            :segment="segment"
                            :stack="stack"
                            :colors="colors"
                            :dark="dark"
                            :is-desktop="false"
                        />
                    </div>
                </button>
            </div>

            <template v-if="hasGuidedTemplates">
                <h3 class="mb-2 mt-6 text-sm font-semibold text-theme-base">
                    Bloques guiados
                </h3>
                <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <button
                        v-for="template in guidedTemplates"
                        :key="template.id"
                        type="button"
                        class="flex flex-col overflow-hidden rounded-xl border border-theme-border text-left transition hover:border-theme-primary hover:shadow-sm"
                        @click="chooseGuidedTemplate(template)"
                    >
                        <span
                            :title="template.description"
                            class="border-b border-theme-border px-3 py-2 text-sm font-semibold text-theme-base"
                        >
                            {{ template.name }}
                        </span>
                        <div
                            class="pointer-events-none max-h-64 overflow-y-auto"
                        >
                            <segment-preview
                                :segment="guidedPreviewSegment(template)"
                                :stack="stack"
                                :colors="colors"
                                :dark="dark"
                                :is-desktop="false"
                            />
                        </div>
                    </button>
                </div>
            </template>
        </div>

        <div v-else-if="step === 'catalog-mode'" class="space-y-2">
            <button
                type="button"
                class="flex w-full items-start gap-3 rounded-xl border border-theme-border p-3 text-left transition hover:border-theme-primary hover:bg-theme-hover"
                @click="applyCatalogTemplate('append')"
            >
                <span class="text-xl">⬇️</span>
                <span class="min-w-0 flex-1">
                    <span class="block text-sm font-semibold text-theme-base">
                        Agregar al final
                    </span>
                    <span class="block text-xs text-theme-muted">
                        Los bloques de la plantilla se suman después de los que
                        ya tienes.
                    </span>
                </span>
            </button>
            <button
                type="button"
                class="flex w-full items-start gap-3 rounded-xl border border-theme-border p-3 text-left transition hover:border-theme-primary hover:bg-theme-hover"
                @click="applyCatalogTemplate('prepend')"
            >
                <span class="text-xl">⬆️</span>
                <span class="min-w-0 flex-1">
                    <span class="block text-sm font-semibold text-theme-base">
                        Agregar al inicio
                    </span>
                    <span class="block text-xs text-theme-muted">
                        Los bloques de la plantilla se suman antes de los que ya
                        tienes.
                    </span>
                </span>
            </button>
            <button
                type="button"
                class="flex w-full items-start gap-3 rounded-xl border border-theme-border p-3 text-left transition hover:bg-theme-hover"
                @click="applyCatalogTemplate('replace')"
            >
                <span class="text-xl">♻️</span>
                <span class="min-w-0 flex-1">
                    <span class="block text-sm font-semibold text-theme-danger">
                        Reemplazar todo
                    </span>
                    <span class="block text-xs text-theme-muted">
                        Borra los bloques actuales y los cambia por los de la
                        plantilla.
                    </span>
                </span>
            </button>
        </div>

        <div v-else class="space-y-4">
            <custom-input
                v-for="field in selectedGuidedTemplate.fields"
                :key="field.key"
                v-model="guidedFieldValues[field.key]"
                :label="field.label"
                :placeholder="field.placeholder"
            />
        </div>

        <template #footer>
            <div class="flex justify-end gap-2">
                <button
                    v-if="step === 'guided-form' || step === 'catalog-mode'"
                    class="rounded-lg border border-theme-border px-4 py-2 text-sm font-medium text-theme-muted transition hover:bg-theme-hover"
                    @click="goBackToList"
                >
                    Volver
                </button>
                <button
                    class="rounded-lg border border-theme-border px-4 py-2 text-sm font-medium text-theme-muted transition hover:bg-theme-hover"
                    @click="isOpen = false"
                >
                    Cancelar
                </button>
                <app-button
                    v-if="step === 'guided-form'"
                    color="success"
                    :loading="isSubmittingGuided"
                    :disabled="!canConfirmGuided"
                    @click="confirmGuidedTemplate"
                >
                    Agregar bloque
                </app-button>
            </div>
        </template>
    </AppDialog>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import {
    CATALOG_TEMPLATES,
    GUIDED_SEGMENT_TEMPLATES
} from "@/common/fields/segment-templates";

import SegmentPreview from "@/components/editor/SegmentPreview.vue";
import AppDialog from "@/components/utils/AppDialog.vue";
import CustomInput from "@/components/utils/CustomInput.vue";

export default {
    name: "SegmentTemplatesModal",
    components: { AppDialog, CustomInput, SegmentPreview },
    model: {
        prop: "value",
        event: "input"
    },
    props: {
        value: {
            type: Boolean,
            default: false
        },
        segments: {
            type: Array,
            default: () => []
        },
        stack: {
            type: Object,
            required: true
        },
        colors: {
            type: Object,
            required: true
        },
        dark: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            step: "list",
            selectedCatalogTemplate: null,
            selectedGuidedTemplate: null,
            guidedFieldValues: {},
            isSubmittingGuided: false
        };
    },
    computed: {
        ...mapGetters("control", ["backgroundColor"]),
        isOpen: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit("input", value);
            }
        },
        catalogTemplates() {
            return CATALOG_TEMPLATES;
        },
        guidedTemplates() {
            return GUIDED_SEGMENT_TEMPLATES;
        },
        hasGuidedTemplates() {
            return GUIDED_SEGMENT_TEMPLATES.length > 0;
        },
        canConfirmGuided() {
            if (!this.selectedGuidedTemplate) return false;
            return this.selectedGuidedTemplate.fields.every((field) =>
                (this.guidedFieldValues[field.key] || "").trim()
            );
        },
        modalTitle() {
            if (this.step === "guided-form") {
                return this.selectedGuidedTemplate.name;
            }
            if (this.step === "catalog-mode") {
                return this.selectedCatalogTemplate.name;
            }
            return "Usar plantilla";
        }
    },
    methods: {
        ...mapActions("customLinks", ["createCustomLink"]),
        resetAll() {
            this.step = "list";
            this.selectedCatalogTemplate = null;
            this.selectedGuidedTemplate = null;
            this.guidedFieldValues = {};
            this.isSubmittingGuided = false;
        },
        guidedPreviewSegment(template) {
            return {
                type: template.segmentType,
                style: template.style,
                links: [
                    {
                        id: "preview",
                        title: template.linkTitle,
                        url: "#",
                        imageUrl: template.imageUrl || null
                    }
                ]
            };
        },
        chooseCatalogTemplate(template) {
            this.selectedCatalogTemplate = template;
            if (!this.segments.length) {
                this.applyCatalogTemplate("replace");
                return;
            }
            this.step = "catalog-mode";
        },
        applyCatalogTemplate(mode) {
            const template = this.selectedCatalogTemplate;
            const cloned = JSON.parse(JSON.stringify(template.segments));
            this.$emit("apply-full", { mode, segments: cloned });
            this.isOpen = false;
        },
        chooseGuidedTemplate(template) {
            this.selectedGuidedTemplate = template;
            this.guidedFieldValues = Object.fromEntries(
                template.fields.map((field) => [field.key, ""])
            );
            this.step = "guided-form";
        },
        goBackToList() {
            this.step = "list";
            this.selectedCatalogTemplate = null;
            this.selectedGuidedTemplate = null;
            this.guidedFieldValues = {};
        },
        async confirmGuidedTemplate() {
            if (!this.canConfirmGuided || this.isSubmittingGuided) return;

            const template = this.selectedGuidedTemplate;
            this.isSubmittingGuided = true;
            try {
                const link = await this.createCustomLink({
                    type: "banner",
                    title: template.linkTitle,
                    entity: "external",
                    url: this.guidedFieldValues[template.fields[0].key],
                    imageId: template.imageId
                });
                this.isSubmittingGuided = false;
                this.$emit("apply-single", {
                    type: template.segmentType,
                    style: template.style,
                    links: [link]
                });
                this.isOpen = false;
            } catch (error) {
                this.isSubmittingGuided = false;
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        }
    }
};
</script>
